// GRP "Apply QA rows": give a boat already on the sheet the QA inspection task rows of another boat — one
// added without them, added by hand in Smartsheet, or missing a section. Pick the boat and the boat to take
// the rows from (by default the latest boat of the same kind, MK- or not, with a full set), then the sections. Only the rows
// the boat doesn't have yet are added, each where it sits on the other boat; the rows it has, and their
// results (status, defects, inspector, dates), stay as they are (grpMergePlan in
// packages/shared/src/domain/grpBoat.ts; the worker's /api/grp/apply-tasks does the same on a fresh read).
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { countTasks, defaultTemplateBoat, grpMergePlan, grpTaskTree, grpTemplateBoats, isMkBoat } from '@technohull/shared';
import { useGrpApplyTasks, type SheetGridRow } from '../features/hooks.js';
import { InlineError, Modal } from '../components/ui.js';
import { IconCheck } from '../components/icons.js';

const cellText = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const rowsLabel = (n: number) => `${n} row${n === 1 ? '' : 's'}`;

export function GrpApplyTasks({ rows, primaryId, boatId, onClose, onApplied }: {
  rows: SheetGridRow[];
  primaryId: number;
  /** the boat it opened on (null: pick one) */
  boatId: number | null;
  onClose: () => void;
  onApplied: (boatId: number) => void;
}) {
  const apply = useGrpApplyTasks();
  const nameOf = (id: number) => cellText(rows.find((r) => r.id === id)?.cells[primaryId]) || '(unnamed)';
  // every boat, the latest first
  const boats = useMemo(() => rows.filter((r) => r.parentId == null).map((r) => ({ id: r.id, name: cellText(r.cells[primaryId]) })).reverse(), [rows, primaryId]);
  const [target, setTarget] = useState<number | null>(boatId);
  const [chosen, setChosen] = useState<number | null>(null);
  // the sections ticked; null until someone changes them: then every section with rows to add
  const [ticked, setTicked] = useState<Set<number> | null>(null);

  const targetName = target != null ? nameOf(target) : '';
  const sources = useMemo(() => grpTemplateBoats(rows, primaryId).filter((b) => b.id !== target), [rows, primaryId, target]);
  const auto = target != null ? defaultTemplateBoat(sources, targetName) : null;
  const sourceId = chosen != null && sources.some((b) => b.id === chosen) ? chosen : auto?.id ?? null;
  const source = sources.find((b) => b.id === sourceId) ?? null;
  const tree = useMemo(() => (sourceId != null ? grpTaskTree(rows, primaryId, sourceId) : []), [rows, primaryId, sourceId]);
  // each section: its rows, and how many of them the boat is missing
  const sections = useMemo(
    () => (target == null ? [] : tree.filter((n) => n.id != null).map((n) => {
      const p = grpMergePlan(rows, primaryId, target, tree, new Set([n.id!]));
      return { id: n.id!, name: n.name, rows: countTasks([n]), add: p.add };
    })),
    [rows, primaryId, target, tree],
  );
  const picked = useMemo(() => ticked ?? new Set(sections.filter((s) => s.add > 0).map((s) => s.id)), [ticked, sections]);
  const plan = useMemo(() => (target == null ? null : grpMergePlan(rows, primaryId, target, tree, picked)), [rows, primaryId, target, tree, picked]);
  const missingIds = sections.filter((s) => s.add > 0).map((s) => s.id);

  const toggle = (id: number) => setTicked(() => { const n = new Set(picked); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pickBoat = (id: number | null) => { setTarget(id); setChosen(null); setTicked(null); };
  const pickSource = (id: number) => { setChosen(id); setTicked(null); };
  const submit = () => {
    if (target == null || sourceId == null || !plan?.add) return;
    apply.mutate({ boatId: target, templateId: sourceId, sections: [...picked] }, { onSuccess: () => { onApplied(target); onClose(); } });
  };
  const mk = isMkBoat(targetName);

  return createPortal(
    <Modal
      title="Apply QA rows from another boat"
      onClose={onClose}
      width={600}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!plan?.add || apply.isPending} onClick={submit}>
            {apply.isPending ? 'Adding…' : plan?.add ? `Add ${rowsLabel(plan.add)}` : 'Nothing to add'}
          </button>
        </>
      )}
    >
      <div className="gab gat">
        <label className="gab-field">
          <span>Boat</span>
          <select className="field" value={target ?? ''} autoFocus={target == null} onChange={(e) => pickBoat(e.currentTarget.value ? Number(e.currentTarget.value) : null)}>
            <option value="">Choose the boat to give the rows to</option>
            {boats.map((b) => <option key={b.id} value={b.id}>{b.name || '(unnamed)'}</option>)}
          </select>
        </label>
        {target != null && (
          <label className="gab-field">
            <span>Take the QA rows from</span>
            <select className="field" value={sourceId ?? ''} onChange={(e) => pickSource(Number(e.currentTarget.value))}>
              {sourceId == null && <option value="">No other boat has QA rows yet</option>}
              {(['mk', 'other'] as const).map((kind) => {
                const list = sources.filter((b) => b.mk === (kind === 'mk')).slice().reverse();
                return list.length ? (
                  <optgroup key={kind} label={kind === 'mk' ? 'MK- boats (their own tasks)' : 'Other boats'}>
                    {list.map((b) => <option key={b.id} value={b.id}>{b.name || '(unnamed)'} · {rowsLabel(b.tasks)}</option>)}
                  </optgroup>
                ) : null;
              })}
            </select>
            {source && chosen == null && <small className="muted">The latest {mk ? 'MK- boat' : 'boat without “MK-”'} with a full set of QA rows, as {mk ? 'this one is an MK- boat' : 'this one isn’t an MK- boat'}.</small>}
            {!auto && chosen == null && sources.length > 0 && (
              <small className="gab-note">{mk ? 'No other MK- boat has QA rows yet' : 'No other non-MK boat has QA rows yet'}: pick one above.</small>
            )}
          </label>
        )}

        {target != null && source && (
          <fieldset className="gab-tasks">
            <legend>Sections</legend>
            {missingIds.length > 1 && (
              <div className="gat-all">
                <button type="button" className="gat-link" onClick={() => setTicked(new Set(missingIds))}>All missing</button>
                <button type="button" className="gat-link" onClick={() => setTicked(new Set())}>None</button>
              </div>
            )}
            <ul className="gat-list">
              {sections.map((s) => {
                const has = s.add === 0;
                return (
                  <li key={s.id}>
                    <label className={`gat-sec${has ? ' is-there' : ''}`}>
                      <input type="checkbox" checked={!has && picked.has(s.id)} disabled={has} onChange={() => toggle(s.id)} />
                      <span className="gat-name">{s.name || '(no name)'}</span>
                      <span className="gat-count">
                        {has
                          ? <><IconCheck size={13} /> on the boat</>
                          : s.add === s.rows ? `${rowsLabel(s.add)} to add` : `${s.add} of ${s.rows} to add`}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
            {plan && (
              <p className="muted gab-sum">
                {plan.add
                  ? <>Adds <b>{rowsLabel(plan.add)}</b> to <b>{targetName}</b>, names only, each where it sits on {source.name || 'the other boat'}.</>
                  : missingIds.length ? 'Tick the sections to add.' : <><b>{targetName}</b> already has every QA row of {source.name || 'that boat'}.</>}
                {plan.existing === 1 && <> The 1 row it already has stays as it is, with its results.</>}
                {plan.existing > 1 && <> The {plan.existing} rows it already has stay as they are, with their results.</>}
              </p>
            )}
          </fieldset>
        )}
        {apply.isError && <InlineError error={apply.error} onDismiss={() => apply.reset()} />}
      </div>
    </Modal>,
    document.body,
  );
}
